import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Link2, MapPin, Send, Settings, ScrollText, Copy, ChevronLeft, ChevronRight, Layers } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import { LogoutButton } from '@/components/auth/LogoutButton';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { RestrictionChips } from '@/components/layout/RestrictionChips';

interface LayoutProps {
  children: ReactNode;
}

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Partner Accounts', href: '/partner-accounts', icon: Link2 },
  { name: 'Synced Locations', href: '/synced-locations', icon: MapPin },
  { name: 'Batch Operations', href: '/batch-operations', icon: Layers },
  { name: 'Duplicates', href: '/duplicates', icon: Copy },
  { name: 'Booking Push Logs', href: '/booking-push-logs', icon: Send },
  { name: 'Audit Log', href: '/audit-log', icon: ScrollText },
  { name: 'Settings', href: '/settings', icon: Settings },
];

const SIDEBAR_PINNED_KEY = 'bridge-sidebar-pinned';

export function Layout({ children }: LayoutProps): JSX.Element {
  const location = useLocation();
  const { user } = useAuth();
  // Track if sidebar is pinned open (persisted) vs temporarily expanded on hover
  const [isPinned, setIsPinned] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_PINNED_KEY);
    return saved === 'true';
  });
  const [isHovered, setIsHovered] = useState(false);
  // Temporarily ignore hover after clicking a nav item (resets when mouse leaves)
  const [ignoreHover, setIgnoreHover] = useState(false);

  // Sidebar is expanded if pinned OR (hovered AND not ignoring hover)
  const isExpanded = isPinned || (isHovered && !ignoreHover);

  const togglePin = (): void => {
    setIsPinned((prev) => {
      const newValue = !prev;
      localStorage.setItem(SIDEBAR_PINNED_KEY, String(newValue));
      return newValue;
    });
  };

  return (
    <div className="min-h-screen">
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 bottom-0 z-40 bg-card border-r transition-all duration-300 overflow-hidden",
          isExpanded ? "w-64" : "w-16"
        )}
          onMouseEnter={() => { setIsHovered(true); }}
          onMouseLeave={() => { setIsHovered(false); setIgnoreHover(false); }}
        >
          <div className={cn(
            "flex h-16 items-center border-b",
            isExpanded ? "gap-2 px-6" : "justify-center px-2"
          )}>
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary flex-shrink-0">
              <Link2 className="h-4 w-4 text-primary-foreground" />
            </div>
            {isExpanded && <span className="font-semibold whitespace-nowrap">Partner Bridge</span>}
          </div>
          <nav className={cn(
            "flex-1 space-y-1",
            isExpanded ? "p-4" : "p-2"
          )}>
            <TooltipProvider delayDuration={0}>
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                const linkContent = (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => { if (!isPinned) setIgnoreHover(true); }}
                    className={cn(
                      'flex items-center rounded-lg text-sm font-medium transition-colors whitespace-nowrap',
                      isExpanded ? 'gap-3 px-3 py-2' : 'justify-center p-3',
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    )}
                  >
                    <item.icon className="h-4 w-4 flex-shrink-0" />
                    {isExpanded && item.name}
                  </Link>
                );

                if (!isExpanded) {
                  return (
                    <Tooltip key={item.name}>
                      <TooltipTrigger asChild>
                        {linkContent}
                      </TooltipTrigger>
                      <TooltipContent side="right" sideOffset={8}>
                        {item.name}
                      </TooltipContent>
                    </Tooltip>
                  );
                }

                return linkContent;
              })}
            </TooltipProvider>
          </nav>
          {/* Pin/unpin button - only visible when expanded */}
          {isExpanded && (
            <div className="absolute bottom-4 right-4">
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={togglePin}
                      className="h-8 w-8"
                      aria-label={isPinned ? "Unpin sidebar" : "Pin sidebar open"}
                    >
                      {isPinned ? (
                        <ChevronLeft className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    {isPinned ? "Unpin sidebar" : "Pin sidebar open"}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}
        </aside>

      {/* Header */}
      <header className={cn(
        "fixed right-0 top-0 z-30 flex h-16 items-center gap-4 border-b bg-background px-6 transition-all duration-300",
        isPinned ? "left-64" : "left-16"
      )}>
        <div className="flex-1">
          <h1 className="text-lg font-semibold">
            {navigation.find((item) => item.href === location.pathname)?.name ?? 'Partner Bridge'}
          </h1>
        </div>
        <div className="flex items-center gap-4">
          <RestrictionChips scope="global" />
          {user && (
            <span className="text-sm text-muted-foreground">
              {user.email}
            </span>
          )}
          <LogoutButton variant="ghost" showText={false} />
        </div>
      </header>

      {/* Main content - padding for sidebar and header */}
      <div className={cn(
        "min-h-screen pt-16 transition-all duration-300",
        isPinned ? "pl-64" : "pl-16"
      )}>
        <main className="p-6">{children}</main>
      </div>
    </div>
  );
}
